import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { succesToast, errorToast } from 'basics/utils/toast';
import { Event } from 'modules/events/basics/types/events.types';
import { OnlineWebinarInput } from 'modules/events/basics/types/onlineWebinar.types';
import { eventFragment } from 'modules/events/graphql/fragments/event.fragments';
import { onlineEventDataFragment } from 'modules/events/graphql/fragments/onlineEventData.fragments';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const updateOnlineWebinarMutationCall = gql`
mutation updateOnlineWebinarMutation($onlineEventInput: OnlineEventInput!) {
  updateOnlineEvent(onlineEventInput: $onlineEventInput) {
    ${eventFragment}
    source
    data {
      ...on OnlineEvent {
        ${onlineEventDataFragment}
      }
    }
  }
}
`;

type ResultType = {
  updateOnlineEvent: Event;
};

type UseUpdateOnlineWebinarMutation = [
  (onlineEventInput: OnlineWebinarInput) => Promise<Event | undefined | null>,
  MutationResult<ResultType>,
];

export const useUpdateOnlineWebinarMutation = (): UseUpdateOnlineWebinarMutation => {
  const { t } = useTranslation();
  const [updateOnlineWebinar, state] = useMutation<ResultType>(updateOnlineWebinarMutationCall);
  const executeMutation = useCallback(async (onlineEventInput) => {
    try {
      const data = await updateOnlineWebinar({
        variables: { onlineEventInput },
      });
      succesToast(t('toast_draft_saved'));

      return data.data?.updateOnlineEvent;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error, onlineEventInput);
        Logger.logError({
          error,
          customContext,
          transactionName: 'updateOnlineWebinar.ts - useUpdateOnlineWebinarMutation',
        });
      }
      errorToast(t('toast_draft_saved_error'));
      return null;
    }
  }, [updateOnlineWebinar, t]);

  return [executeMutation, state];
};
